import React, { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const BG_URL =
  "https://raw.githubusercontent.com/atpcummings-code/swordpoint-data/refs/heads/main/spbackground.png";
const LOGO_URL =
  "https://raw.githubusercontent.com/atpcummings-code/swordpoint-data/refs/heads/main/swordpointlogo.png";

const CINZEL = { fontFamily: "'Cinzel', 'EB Garamond', serif", fontVariant: "small-caps" };
const GARAMOND = { fontFamily: "'EB Garamond', serif" };

const PANEL_STYLE = {
  background: "linear-gradient(180deg, #EDE3CB 0%, #E2D2B1 100%)",
  border: "2px solid #AB9268",
  borderRadius: "12px",
  boxShadow:
    "0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 4px rgba(74,58,40,0.22)",
};

const INPUT_CLASS =
  "bg-[#FAF6ED] border-[#AB9268] text-[#16120E] placeholder:text-[#6B583E]/60 focus-visible:ring-[#8A734C]";

export function LoginScreen() {
  const { login, register, authError } = useAuth();
  const [mode, setMode] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    if (mode === "signin") await login(email, password);
    else await register(email, password);
    setSubmitting(false);
  };

  const tabClass = (active) =>
    `flex-1 rounded-full py-1.5 text-base font-semibold transition-colors ${
      active ? "bg-[#8A734C] text-[#FAF6ED]" : "text-[#4A3A28] hover:bg-[#8A734C]/15"
    }`;

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: "#020617",
        backgroundImage: `url('${BG_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
      }}
    >
      <main className="flex-1 flex flex-wrap items-stretch justify-evenly content-center gap-6 px-4 py-10">
        <div
          data-testid="login-screen"
          className="w-[440px] max-w-full p-8"
          style={PANEL_STYLE}
        >
          <div className="flex flex-col items-center gap-0.5 mb-6">
            <img
              data-testid="login-logo"
              src={LOGO_URL}
              alt="Swordpoint"
              className="h-24 w-auto object-contain"
            />
            <span
              data-testid="login-subtitle"
              className="text-[14.67px] leading-none"
              style={{
                marginTop: "-16px",
                color: "#219cd7",
                fontFamily: "'Cinzel', 'EB Garamond', serif",
                fontVariant: "small-caps",
                textShadow: "0 1px 2px rgba(22,18,14,0.35)",
              }}
            >
              Army List Builder
            </span>
          </div>

          <div className="flex rounded-full bg-[#F5EFE0] border border-[#AB9268] p-1 mb-6" style={CINZEL}>
            <button
              type="button"
              data-testid="auth-mode-signin-tab"
              onClick={() => setMode("signin")}
              className={tabClass(mode === "signin")}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid="auth-mode-register-tab"
              onClick={() => setMode("register")}
              className={tabClass(mode === "register")}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="login-email"
                className="text-sm font-semibold tracking-wider text-[#4A3A28]"
                style={CINZEL}
              >
                Email
              </Label>
              <Input
                id="login-email"
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT_CLASS}
                style={GARAMOND}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="login-password"
                className="text-sm font-semibold tracking-wider text-[#4A3A28]"
                style={CINZEL}
              >
                Password
              </Label>
              <Input
                id="login-password"
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={INPUT_CLASS}
                style={GARAMOND}
                placeholder="••••••••"
              />
            </div>

            {authError && (
              <div
                data-testid="auth-error"
                className="rounded-md border border-[#9E2A2B]/50 bg-[#F6E3E1] text-[#9E2A2B] px-3 py-2 text-sm flex items-start gap-2"
                style={GARAMOND}
              >
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={submitting}
              className="w-full rounded-full bg-[#219cd7] hover:bg-[#1a86bb] text-white font-semibold py-2 h-auto text-base"
              style={CINZEL}
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {mode === "signin" ? "Sign In" : "Create Account"}
            </Button>
          </form>
        </div>

        <div
          data-testid="login-right-panel"
          className="w-[440px] max-w-full p-8"
          style={PANEL_STYLE}
        >
          <h2
            data-testid="login-welcome-title"
            className="text-center text-2xl font-bold pb-2 mb-5"
            style={{
              color: "#219cd7",
              fontFamily: "'Cinzel', 'EB Garamond', serif",
              borderBottom: "2px solid #219cd7",
            }}
          >
            Welcome
          </h2>
          <div
            data-testid="login-welcome-body"
            className="space-y-4 text-left text-lg"
            style={{ ...GARAMOND, color: "#4A3A28", lineHeight: 1.6 }}
          >
            <p>
              Swordpoint is a set of "Big Battle" game rules for fighting actions in the ancient and
              medieval periods.
            </p>
            <p>
              This army list builder is approved by Gripping Beast Plastics, and Martin Gibbins the
              author of the Swordpoint rules for use in all tournaments and competitions.
            </p>
            <p>
              As with all things we are only human and inevitably make mistakes, if you spot any
              within the army builder please get in touch (
              <a
                href="mailto:andy@armybuilder.net"
                data-testid="login-contact-email"
                className="underline"
              >
                andy@armybuilder.net
              </a>
              ) and detail the issue
              so it can be fixed - thanks.
            </p>
          </div>
        </div>
      </main>

      <footer
        data-testid="login-footer"
        className="px-4 pb-4 text-center text-white"
        style={{ ...GARAMOND, fontSize: "9pt" }}
      >
        Swordpoint is Copyright © Gripping Beast Plastics {new Date().getFullYear()}. The Swordpoint
        Army Builder is Copyright © Tradestands Ltd {new Date().getFullYear()}.
      </footer>
    </div>
  );
}
